import {type ReactElement} from 'react'
import {Linking, Pressable, View} from 'react-native'
import {type ComAtprotoServerDescribeServer} from '@atproto/api'
import {msg, Trans} from '@lingui/macro'
import {useLingui} from '@lingui/react'

import {isWeb} from '#/platform/detection'
import {atoms as a, useTheme} from '#/alf'
import {CircleInfo_Stroke2_Corner0_Rounded as CircleInfo} from '#/components/icons/CircleInfo'
import {Text} from '#/components/Typography'

export const Policies = ({
  serviceDescription,
  under18,
}: {
  serviceDescription: ComAtprotoServerDescribeServer.OutputSchema
  under18: boolean
}) => {
  const t = useTheme()
  const {_} = useLingui()

  if (!serviceDescription) {
    return <View />
  }

  const tos = serviceDescription.links?.termsOfService
  const pp = serviceDescription.links?.privacyPolicy

  const handleTosPress = () => {
    if (tos) {
      if (isWeb) {
        window.open(tos, '_blank', 'noopener,noreferrer')
      } else {
        Linking.openURL(tos)
      }
    }
  }

  const handlePrivacyPress = () => {
    if (pp) {
      if (isWeb) {
        window.open(pp, '_blank', 'noopener,noreferrer')
      } else {
        Linking.openURL(pp)
      }
    }
  }

  if (!tos && !pp) {
    return (
      <View style={[a.flex_row, a.align_center, a.gap_xs]}>
        <CircleInfo size="md" fill={t.atoms.text_contrast_low.color} />

        <Text style={[t.atoms.text_contrast_medium]}>
          <Trans>
            This service has not provided terms of service or a privacy policy.
          </Trans>
        </Text>
      </View>
    )
  }

  let els: ReactElement
  if (tos && pp) {
    els = (
      <View>
        <Text style={[a.leading_snug, t.atoms.text_contrast_medium]}>
          <Trans>By creating an account you agree to the</Trans>{' '}
          <Pressable
            onPress={handleTosPress}
            accessibilityRole="button"
            accessibilityLabel={_(msg`Read the Terms of Service`)}
            accessibilityHint={_(msg`Opens the Terms of Service document`)}
            style={{cursor: 'pointer'}}>
            <Text
              style={[
                a.leading_snug,
                {color: t.palette.primary_500, textDecorationLine: 'underline'},
              ]}>
              <Trans>Terms of Service</Trans>
            </Text>
          </Pressable>{' '}
          <Trans>and</Trans>{' '}
          <Pressable
            onPress={handlePrivacyPress}
            accessibilityRole="button"
            accessibilityLabel={_(msg`Read the Privacy Policy`)}
            accessibilityHint={_(msg`Opens the Privacy Policy document`)}
            style={{cursor: 'pointer'}}>
            <Text
              style={[
                a.leading_snug,
                {color: t.palette.primary_500, textDecorationLine: 'underline'},
              ]}>
              <Trans>Privacy Policy</Trans>
            </Text>
          </Pressable>
          .
        </Text>
      </View>
    )
  } else if (tos) {
    els = (
      <View>
        <Text style={[a.leading_snug, t.atoms.text_contrast_medium]}>
          <Trans>By creating an account you agree to the</Trans>{' '}
          <Pressable
            onPress={handleTosPress}
            accessibilityRole="button"
            accessibilityLabel={_(msg`Read the Terms of Service`)}
            accessibilityHint={_(msg`Opens the Terms of Service document`)}
            style={{cursor: 'pointer'}}>
            <Text
              style={[
                a.leading_snug,
                {color: t.palette.primary_500, textDecorationLine: 'underline'},
              ]}>
              <Trans>Terms of Service</Trans>
            </Text>
          </Pressable>
          .
        </Text>
      </View>
    )
  } else if (pp) {
    els = (
      <View>
        <Text style={[a.leading_snug, t.atoms.text_contrast_medium]}>
          <Trans>By creating an account you agree to the</Trans>{' '}
          <Pressable
            onPress={handlePrivacyPress}
            accessibilityRole="button"
            accessibilityLabel={_(msg`Read the Privacy Policy`)}
            accessibilityHint={_(msg`Opens the Privacy Policy document`)}
            style={{cursor: 'pointer'}}>
            <Text
              style={[
                a.leading_snug,
                {color: t.palette.primary_500, textDecorationLine: 'underline'},
              ]}>
              <Trans>Privacy Policy</Trans>
            </Text>
          </Pressable>
          .
        </Text>
      </View>
    )
  } else {
    return null
  }

  return (
    <View style={[a.gap_sm]}>
      {els}

      {under18 ? (
        <Text style={[a.font_bold, a.leading_snug, t.atoms.text_contrast_high]}>
          <Trans>
            You must be 18 years of age or older to create an account.
          </Trans>
        </Text>
      ) : undefined}
    </View>
  )
}
